import React from "react";
import PropTypes from "prop-types";
import { ProducerReportsTable } from "common/components/producer";
import { TableNoDataOverlay } from "common/components";
import { ProducerRevenueLoader, ProducerSummaryPreview } from "application/producer/components";
import { MrbRouterLink } from "mrb/core/infrastructure/router";
import { MrbContentLoader } from "mrb/components/loader";
import { MrbSelect } from "mrb/components/select";
import { MrbToggle } from "mrb/components/checkbox";
import { ProducerAccountPreviewBalance, ProducerAccountPreviewRevenueTransaction } from "common/components/producer";
import { defaultTemplate } from "common/hoc";
import { observer } from "mobx-react";
import { MrbButton } from "mrb";
function ProducerRevenueTemplate({ producerRevenueViewStore, t }) {
    const { reportsGridStore, revenueGridStore, balanceGridStore } = producerRevenueViewStore;

    return (
        <div className="c-container--sidebar u-padd--none">
            <div className="u-overflow--hidden">
                <div className="u-mar--bottom--sml">
                    <div>
                        <h1 className="u-type--base u-mar--bottom--sml">
                            {t("APPLICATION.PRODUCER.REVENUE.REPORTS_TITLE")}
                        </h1>
                        <div className="u-mar--bottom--sml">
                            <ProducerReportsTable
                                gridStore={reportsGridStore}
                                emptyStateRenderer={TableNoDataOverlay}
                                t={t}
                            />
                        </div>
                    </div>
                </div>

                <div className="u-mar--bottom--sml">
                    <ProducerAccountPreviewRevenueTransaction
                        title="APPLICATION.PRODUCER.REVENUE.REVENUE_TRANSACTIONS_TITLE"
                        gridStore={revenueGridStore}
                        emptyStateRenderer={TableNoDataOverlay}
                        t={t}
                    />
                </div>
                <ProducerAccountPreviewBalance
                    title="APPLICATION.PRODUCER.REVENUE.ACCOUNT_BALANCE_TITLE"
                    gridStore={balanceGridStore}
                    emptyStateRenderer={TableNoDataOverlay}
                    t={t}
                />
            </div>

            <div>
                <div className="row">
                    <div className="col col-sml-12 col-lrg-6 col-xxlrg-12">
                        <ProducerSummaryPreview producerRevenueViewStore={producerRevenueViewStore} />
                    </div>
                </div>

                <Actions producerRevenueViewStore={producerRevenueViewStore} t={t} />
            </div>
        </div>
    );
}

ProducerRevenueTemplate.propTypes = {
    producerRevenueViewStore: PropTypes.object.isRequired,
    t: PropTypes.func,
};

const Actions = observer(function Actions({ producerRevenueViewStore, t }) {
    const {
        payoutsEnabled,
        isStripeAccountConnected,
        createStripeAccount,
        stripeAccountLink,
        isCreateStripeAccountButtonDisabled,
        loaderStore,
        restartStripeConnect,
        isRestartStripeConnectInProgress,
        countrySelectStore,
        isCountrySelectionValid,
        isAutomaticPayoutEnabled,
        isAutomaticPayoutUpdateInProgress,
        toggleAutomaticPayout,
    } = producerRevenueViewStore;
    return (
        <div>
            <h1 className="u-type--base u-mar--bottom--sml">Actions</h1>
            <div className="c-card--primary c-card--med">
                <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <ProducerRevenueLoader />}>
                    {() => (
                        <div className="row">
                            <div className="col col-sml-12 col-med-6 col-lrg-12">
                                {!isStripeAccountConnected && (
                                    <div>
                                        <p className="u-type--base u-mar--bottom--xsml">Set up payouts</p>
                                        <p className="u-type--sml u-type--color--opaque u-mar--bottom--sml">
                                            Choose the country for your bank account, then link it to Stripe.
                                        </p>
                                        <MrbSelect
                                            id="producer-country"
                                            aria-label="Country"
                                            store={countrySelectStore}
                                            placeholder="Select your country"
                                            classNameExtend="c-dropdown--dark c-dropdown--sml-custom c-dropdown--full u-mar--bottom--sml"
                                            t={t}
                                        />
                                        <MrbButton
                                            t={t}
                                            label="Link bank account"
                                            onClick={createStripeAccount}
                                            aria-busy={isCreateStripeAccountButtonDisabled}
                                            className="c-btn c-btn--base c-btn--primary c-btn--full u-mar--bottom--xsml"
                                            disabled={isCreateStripeAccountButtonDisabled || !isCountrySelectionValid}
                                        />
                                    </div>
                                )}
                                {isStripeAccountConnected && !payoutsEnabled && (
                                    <div>
                                        <p className="u-type--base u-mar--bottom--xsml">
                                            Finish setting up payouts
                                        </p>
                                        <p className="u-type--sml u-type--color--opaque u-mar--bottom--sml">
                                            Continue on Stripe to finish verifying your account and enable payouts.
                                        </p>
                                        <MrbButton
                                            t={t}
                                            label="Continue on Stripe"
                                            onClick={() => (window.location.href = stripeAccountLink)}
                                            className="c-btn c-btn--base c-btn--primary c-btn--full"
                                        />

                                        <div className="u-mar--top--med">
                                            <p className="u-type--sml u-type--color--opaque u-mar--bottom--sml">
                                                Stuck, or picked the wrong country? Restart the setup to start over
                                                with a fresh account.
                                            </p>
                                            <MrbButton
                                                t={t}
                                                label="Restart account setup"
                                                onClick={restartStripeConnect}
                                                aria-busy={isRestartStripeConnectInProgress}
                                                disabled={isRestartStripeConnectInProgress}
                                                className="c-btn c-btn--base c-btn--ghost c-btn--full"
                                            />
                                        </div>
                                    </div>
                                )}
                                {isStripeAccountConnected && payoutsEnabled && (
                                    <div>
                                        <MrbRouterLink
                                            routeName="master.application.producer.payout"
                                            className="c-btn c-btn--base c-btn--primary c-btn--full"
                                        >
                                            Pay out balance
                                        </MrbRouterLink>

                                        <div className="flex justify-between items-start u-gap--lrg u-mar--top--med">
                                            <div>
                                                <p className="u-type--base u-mar--bottom--xsml">
                                                    Automatic monthly payouts
                                                </p>
                                                <p className="u-type--sml u-type--color--opaque">
                                                    Automatically sent to your Stripe account on the 1st of each
                                                    month, as long as your balance is at least $100.
                                                </p>
                                            </div>
                                            <MrbToggle
                                                id="automatic-payout"
                                                value={isAutomaticPayoutEnabled}
                                                disabled={isAutomaticPayoutUpdateInProgress}
                                                onChange={(event) => {
                                                    event.preventDefault();
                                                    toggleAutomaticPayout();
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </MrbContentLoader>
            </div>
        </div>
    );
});

export default defaultTemplate(ProducerRevenueTemplate);
